import React, { useState } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardActionArea,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import SendIcon from '@mui/icons-material/Send'
import StorageIcon from '@mui/icons-material/Storage'
import UploadFileIcon from '@mui/icons-material/UploadFile'

import { AutoCoderImportBatch } from '../../../types/gotypes'
import { ApifyIngestRequest, CarbonIngestRequest, useIngestSources } from '../../../hooks/useIngestSources'
import useSnackbar from '../../../hooks/useSnackbar'
import { importBatchIsInFlight } from '../../../utils/importBatches'
import ApifyConfig from './ApifyConfig'
import CarbonConfig from './CarbonConfig'
import CsvUploadPanel from './CsvUploadPanel'

/**
 * "Add data" — the one door into a job's data, whichever source it comes from.
 *
 * The three sources are the three ways data reaches a job by hand (a file, a
 * scraper, this job's own survey answers), and they are deliberately siblings
 * here rather than three separate flows: what a source IS matters while you are
 * filling the form in, and stops mattering the moment it produces a receipt.
 *
 * None of them sends anything to a pipeline. Coding is a separate, later,
 * deliberate act — which is exactly what the "Send to pipeline" affordance on
 * the result is for.
 */

type AddDataSource = 'csv' | 'apify' | 'carbon'

const SOURCE_TILES: {
  id: AddDataSource
  title: string
  blurb: string
  icon: React.ReactNode
}[] = [
  {
    id: 'csv',
    title: 'Upload a file',
    blurb: 'A CSV, TSV or Excel export. One row becomes one record.',
    icon: <UploadFileIcon sx={{ fontSize: 32, color: 'info.main' }} />,
  },
  {
    id: 'apify',
    title: 'Social comments',
    blurb: 'Scrape the comments on a TikTok, Instagram, YouTube or Facebook post.',
    icon: <ChatBubbleOutlineIcon sx={{ fontSize: 32, color: 'info.main' }} />,
  },
  {
    id: 'carbon',
    title: 'Survey variable',
    blurb: "An open-ended variable from this job's case data. One case, one record.",
    icon: <StorageIcon sx={{ fontSize: 32, color: 'info.main' }} />,
  },
]

const SOURCE_TITLES: Record<AddDataSource, string> = {
  csv: 'Upload a file',
  apify: 'Social comments',
  carbon: 'Survey variable',
}

interface AddDataDialogProps {
  open: boolean
  customer: string
  job: string
  onClose: () => void
  /**
   * Opens the send-to-pipeline flow for the receipt just created. Like the
   * receipts table's row action, the affordance is rendered only when a handler
   * is supplied, so the result never offers a door that leads nowhere.
   */
  onSendToPipeline?: (batch: AutoCoderImportBatch) => void
}

const AddDataDialog: React.FC<AddDataDialogProps> = ({
  open,
  customer,
  job,
  onClose,
  onSendToPipeline,
}) => {
  const snackbar = useSnackbar()
  const { ingestCsv, ingestApify, ingestCarbon, pending, error } = useIngestSources(customer, job)

  const [source, setSource] = useState<AddDataSource | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [apify, setApify] = useState<ApifyIngestRequest>({ platform: 'tiktok', url: '' })
  const [carbon, setCarbon] = useState<CarbonIngestRequest>({ variable: '' })
  const [result, setResult] = useState<AutoCoderImportBatch | null>(null)

  const reset = () => {
    setSource(null)
    setFile(null)
    setApify({ platform: 'tiktok', url: '' })
    setCarbon({ variable: '' })
    setResult(null)
  }

  const close = () => {
    // A fetch in flight is server-side work; closing the dialog abandons the
    // form, never the fetch.
    reset()
    onClose()
  }

  const canSubmit =
    !pending &&
    ((source === 'csv' && !!file) ||
      (source === 'apify' && apify.url.trim() !== '') ||
      (source === 'carbon' && carbon.variable.trim() !== ''))

  const submit = async () => {
    try {
      let batch: AutoCoderImportBatch | undefined
      if (source === 'csv' && file) {
        batch = await ingestCsv(file)
      } else if (source === 'apify') {
        batch = await ingestApify({ ...apify, url: apify.url.trim() })
      } else if (source === 'carbon') {
        batch = await ingestCarbon({
          variable: carbon.variable.trim(),
          filter: (carbon.filter || '').trim() || undefined,
        })
      }
      if (!batch) return
      setResult(batch)
      if (batch.error) {
        snackbar.error(`The fetch failed: ${batch.error}`)
      } else if (importBatchIsInFlight(batch)) {
        snackbar.info('Fetch started — it will report its outcome in the receipts list.')
      } else {
        snackbar.success(`Added ${(batch.new ?? 0).toLocaleString()} new records.`)
      }
    } catch {
      // Surfaced by the error alert below; a rejected mutation here means the
      // REQUEST was malformed, not that the fetch failed.
    }
  }

  return (
    <Dialog
      open={open}
      onClose={pending ? undefined : close}
      maxWidth="lg"
      fullWidth
      // The dialog carries its own id so a test can scope to it rather than to
      // the heading text, which doubles as the source name once a tile is picked.
      data-testid="autocoder-add-data-dialog"
    >
      <DialogTitle>
        Add data
        <Typography variant="body2" color="text.secondary">
          {result
            ? 'What this fetch produced.'
            : source
              ? SOURCE_TITLES[source]
              : 'Everything added here belongs to the job. Sending it to a pipeline for coding is a separate step.'}
        </Typography>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ display: 'flex', flexDirection: 'column', minHeight: source === 'carbon' && !result ? '60vh' : 0 }}
      >
        {error && !result && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {result && <IngestResult batch={result} />}

        {!result && !source && (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            {SOURCE_TILES.map(tile => (
              <Card key={tile.id} variant="outlined" sx={{ flex: 1 }}>
                <CardActionArea
                  sx={{ p: 3, height: '100%' }}
                  onClick={() => setSource(tile.id)}
                  data-testid={`autocoder-add-data-source-${tile.id}`}
                >
                  <Stack spacing={1}>
                    {tile.icon}
                    <Typography variant="subtitle1">{tile.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {tile.blurb}
                    </Typography>
                  </Stack>
                </CardActionArea>
              </Card>
            ))}
          </Stack>
        )}

        {!result && source === 'csv' && (
          <CsvUploadPanel file={file} onChange={setFile} disabled={pending} />
        )}
        {!result && source === 'apify' && (
          <ApifyConfig request={apify} onChange={setApify} disabled={pending} />
        )}
        {!result && source === 'carbon' && (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <CarbonConfig
              request={carbon}
              onChange={setCarbon}
              customer={customer}
              job={job}
              disabled={pending}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {result ? (
          <>
            <Button color="info" onClick={reset} data-testid="autocoder-add-data-more">
              Add more
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            {onSendToPipeline && !result.error && !importBatchIsInFlight(result) && (
              <Button
                color="info"
                variant="outlined"
                startIcon={<SendIcon />}
                onClick={() => {
                  const batch = result
                  close()
                  onSendToPipeline(batch)
                }}
                data-testid="autocoder-add-data-send"
              >
                Send to pipeline
              </Button>
            )}
            <Button
              variant="contained"
              color="info"
              onClick={close}
              data-testid="autocoder-add-data-done"
            >
              Done
            </Button>
          </>
        ) : (
          <>
            {source && (
              <Button
                color="info"
                onClick={reset}
                disabled={pending}
                data-testid="autocoder-add-data-back"
              >
                Back
              </Button>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Button
              color="info"
              onClick={close}
              disabled={pending}
              data-testid="autocoder-add-data-cancel"
            >
              Cancel
            </Button>
            {source && (
              <Button
                variant="contained"
                color="info"
                // Disabled while a request is in flight: each click on the
                // scraper door buys a real actor run, so a double-click is two
                // fetches and two bills.
                disabled={!canSubmit}
                onClick={() => {
                  void submit()
                }}
                // Stable id, not the label: the label flips to "Adding…" while
                // the fetch is in flight, which is exactly when a test is
                // watching this button.
                data-testid="autocoder-add-data-submit"
              >
                {pending ? 'Adding…' : 'Add data'}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

/**
 * The receipt, read back to the person who just made it. The counts are the
 * same funnel the receipts ledger shows, so the two never tell different
 * stories — and a failed fetch is reported here as the DATA it is, because that
 * is how it arrived (HTTP 200, error on the receipt).
 */
const IngestResult: React.FC<{ batch: AutoCoderImportBatch }> = ({ batch }) => {
  if (batch.error) {
    return (
      <Alert severity="error" data-testid="autocoder-add-data-result-failed">
        <AlertTitle>The fetch failed</AlertTitle>
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {batch.error}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          It is recorded in the receipts list either way — that list is the record of what was
          attempted, not only of what worked.
        </Typography>
      </Alert>
    )
  }

  if (importBatchIsInFlight(batch)) {
    return (
      <Alert severity="info" data-testid="autocoder-add-data-result-inflight">
        <AlertTitle>Fetching…</AlertTitle>
        The scrape is running in the background. It is already in the receipts list, and reports
        what it found there when it finishes.
      </Alert>
    )
  }

  return (
    <Alert
      severity={batch.new > 0 ? 'success' : 'warning'}
      data-testid="autocoder-add-data-result-done"
    >
      <AlertTitle>
        {batch.new > 0
          ? `${batch.new.toLocaleString()} new records added`
          : 'This fetch added no new records'}
      </AlertTitle>
      <Typography variant="body2">
        {batch.fetched.toLocaleString()} fetched · {batch.new.toLocaleString()} new ·{' '}
        {batch.duplicates.toLocaleString()} already here ·{' '}
        {batch.parseSkipped.toLocaleString()} unreadable
      </Typography>
      {batch.new === 0 && batch.duplicates > 0 && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          Everything it found was already in this job, so nothing was added twice.
        </Typography>
      )}
    </Alert>
  )
}

export default AddDataDialog
